import { useAvailabilitySlots } from '@/app/_components/widgets/listing/_hooks/useAvailabilitySlots';
import { getStartOfDayTime } from '@/app/_components/widgets/listing/_utils/time';
import { useInView } from '@/app/_hooks/useIsInView';
import { formatReservationPrice } from '@/app/(booking)/checkout/_utils/checkout';
import { capitalizeWordsInString } from '@/utils/string';
import { ELLoader } from '@davincihealthcare/elty-design-system-react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useEffect, useMemo } from 'react';

type Props = {
  specialityId: string;
  workgroupId: string;
  firstTimeAvailable: number | null | undefined;
  delayAvailability?: number;
  hasDynamicPrice: boolean;
  isLicensedClinic: boolean;
  price?: number;
  hasAvailableSlots: boolean;
  onAvailabilityResolved?: (hasRealAvailability: boolean) => void;
};

const getDateTitle = (date: number) => {
  const todayMs = getStartOfDayTime(Date.now());
  if (date === todayMs) return 'Oggi';

  const tomorrowMs = getStartOfDayTime(Date.now(), 1);
  if (date === tomorrowMs) return 'Domani';

  const dateObj = new Date(date);

  return capitalizeWordsInString(dateObj.toLocaleDateString('it', { day: 'numeric', month: 'short' }));
};

export const CardAvailabilityInfo: React.FC<Props> = ({
  specialityId,
  workgroupId,
  firstTimeAvailable = null,
  delayAvailability,
  hasDynamicPrice,
  isLicensedClinic,
  price,
  hasAvailableSlots,
  onAvailabilityResolved,
}) => {
  const { isInView, ref } = useInView();

  const { isInitialLoading, sortedDates } = useAvailabilitySlots({
    specialityId,
    workgroupId,
    enabled: isInView,
    firstAvailabilityDate: firstTimeAvailable,
    delayAvailability,
  });

  const availabilityInfo = useMemo(() => {
    if (!isInView) {
      return {
        dateTitle: null,
        showGradient: false,
        showLoader: false,
        isAvailable: false,
      };
    }

    let dateTitle: string | null = null;

    if (isInitialLoading && firstTimeAvailable) {
      dateTitle = getDateTitle(getStartOfDayTime(firstTimeAvailable));
    } else if (!isInitialLoading && sortedDates.length > 0) {
      dateTitle = getDateTitle(sortedDates[0]);
    }

    return {
      dateTitle,
      showGradient: isInitialLoading && dateTitle !== null,
      showLoader: isInitialLoading,
      isAvailable: dateTitle !== null,
    };
  }, [isInView, isInitialLoading, firstTimeAvailable, sortedDates]);

  useEffect(() => {
    if (!isInitialLoading && isInView) {
      onAvailabilityResolved?.(availabilityInfo.isAvailable);
    }
  }, [isInitialLoading, isInView, availabilityInfo.isAvailable, onAvailabilityResolved]);

  const isPriceAvailable = typeof price === 'number';
  const priceTitle = hasDynamicPrice ? 'A partire da' : 'Prezzo';

  if (!hasAvailableSlots && !isInitialLoading) return <div ref={ref} />;

  return (
    <div ref={ref} className="w-full transition-all duration-300 lg:mx-auto lg:max-w-sm">
      <div className={clsx('grid grid-cols-2 gap-4', isInitialLoading && 'animate-pulse')}>
        <div className="flex flex-col items-center gap-1 rounded-lg bg-[#F1F7FE] p-2">
          <p className="text-sm font-medium text-[#485161]">{`Prima disponibilit\u00E0`}</p>
          <div className="flex items-center gap-1">
            <p className="text-2xl font-bold leading-9 tracking-normal text-neutral-darker">
              {availabilityInfo.isAvailable ? (
                availabilityInfo.showGradient ? (
                  <span className="gradient-text-animated">{availabilityInfo.dateTitle}</span>
                ) : (
                  availabilityInfo.dateTitle
                )
              ) : (
                <span className="text-neutral-inactive">-</span>
              )}
            </p>
            {availabilityInfo.showLoader && (
              <span className="[&_svg]:!size-4">
                <ELLoader />
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center gap-1 rounded-lg bg-[#F1F7FE] p-2">
          <p className="text-sm font-medium text-[#485161]">{priceTitle}</p>
          <div className="flex items-center gap-1">
            <p className={clsx('text-2xl font-bold leading-9', hasAvailableSlots ? 'text-neutral-darker' : 'text-neutral-inactive')}>
              {isPriceAvailable && isLicensedClinic ? `${formatReservationPrice(price)}€` : '-'}
            </p>
            {hasDynamicPrice && isPriceAvailable && isLicensedClinic && <InformationCircleIcon className="size-4 text-[#485161]" />}
          </div>
        </div>
      </div>
    </div>
  );
};
